import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash2, UserCog, Shield, Eye, Clock, Mail, User, Activity, Send, RefreshCw, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminLayout from '../../components/AdminLayout';
import api from '../../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin', description: 'Full access to all features' },
  { value: 'super_admin', label: 'Super Admin', description: 'Full access + user management' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' }
];

const AdminUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserActivity, setSelectedUserActivity] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin',
    active: true
  });
  const [inviteFormData, setInviteFormData] = useState({
    name: '',
    email: '',
    role: 'admin'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, invitesRes] = await Promise.all([
        api.get('/users'),
        api.get('/invites')
      ]);
      setUsers(usersRes.data);
      setInvites(invitesRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = {};
        if (formData.name !== editingUser.name) updateData.name = formData.name;
        if (formData.email !== editingUser.email) updateData.email = formData.email;
        if (formData.role !== editingUser.role) updateData.role = formData.role;
        if (formData.active !== editingUser.active) updateData.active = formData.active;
        if (formData.password) updateData.password = formData.password;
        
        await api.put(`/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        await api.post('/users', formData);
        toast.success('User created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/invites', inviteFormData);
      toast.success('Invitation sent successfully');
      setInviteDialogOpen(false);
      setInviteFormData({ name: '', email: '', role: 'admin' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await api.post(`/invites/${inviteId}/resend`);
      toast.success('Invitation resent successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend invitation');
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await api.delete(`/invites/${inviteId}`);
      toast.success('Invitation deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invitation');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      active: user.active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleViewActivity = async (user) => {
    setSelectedUserActivity(user);
    setActivityDialogOpen(true);
    setLoadingActivity(true);
    
    try {
      const response = await api.get(`/users/${user.id}/activity?limit=50`);
      setActivityLogs(response.data.activity || []);
    } catch (error) {
      toast.error('Failed to load activity logs');
      setActivityLogs([]);
    } finally {
      setLoadingActivity(false);
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'admin',
      active: true
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionBadgeColor = (action) => {
    if (action.includes('login')) return 'bg-[#EEF0FF] text-[#6B5CE7] border-[#C4BFFF]';
    if (action.includes('created')) return 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]';
    if (action.includes('updated')) return 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]';
    if (action.includes('deleted')) return 'bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]';
    if (action.includes('sent') || action.includes('invite')) return 'bg-[#F0F9FF] text-[#0369A1] border-[#BAE6FD]';
    return 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]';
  };

  const getInviteStatusBadge = (invite) => {
    if (invite.status === 'used') {
      return <Badge className="bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">Accepted</Badge>;
    }
    if (invite.status === 'expired') {
      return <Badge className="bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]">Expired</Badge>;
    }
    return <Badge className="bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]">Pending</Badge>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7B6FE8]"></div>
        </div>
      </AdminLayout>
    );
  }

  const pendingInvites = invites.filter(i => i.status === 'pending');

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-[#1A1A1A] uppercase tracking-wide" style={{fontFamily: "Barlow Condensed, sans-serif"}}>User Management</h1>
            <p className="text-[#6B7280] mt-1">Manage admin users, send invites, and track activity</p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="invite-user-btn"
              onClick={() => setInviteDialogOpen(true)}
              variant="outline"
              className="border-[#7B6FE8] text-[#7B6FE8] hover:bg-[#EEF0FF]"
            >
              <Send className="h-4 w-4 mr-2" />
              Invite User
            </Button>
            <Button
              data-testid="add-user-btn"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="bg-white border border-[#E5E7EB] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#EEF0FF] rounded-lg">
                  <UserCog className="h-5 w-5 text-[#7B6FE8]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{users.length}</p>
                  <p className="text-sm text-[#6B7280]">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#ECFDF5] rounded-lg">
                  <Shield className="h-5 w-5 text-[#065F46]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">
                    {users.filter(u => u.active !== false).length}
                  </p>
                  <p className="text-sm text-[#6B7280]">Active Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#FEF2F2] rounded-lg">
                  <User className="h-5 w-5 text-[#991B1B]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">
                    {users.filter(u => u.role === 'super_admin').length}
                  </p>
                  <p className="text-sm text-[#6B7280]">Super Admins</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#FFFBEB] rounded-lg">
                  <Eye className="h-5 w-5 text-[#92400E]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">
                    {users.filter(u => u.role === 'viewer').length}
                  </p>
                  <p className="text-sm text-[#6B7280]">Viewers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-[#E5E7EB] shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#F0F9FF] rounded-lg">
                  <UserPlus className="h-5 w-5 text-[#0369A1]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{pendingInvites.length}</p>
                  <p className="text-sm text-[#6B7280]">Pending Invites</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs for Users and Invites */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F3F4F6] p-1">
            <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1A1A]">
              Users ({users.length})
            </TabsTrigger>
            <TabsTrigger value="invites" className="data-[state=active]:bg-white data-[state=active]:text-[#1A1A1A]">
              Invites ({invites.length})
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="border-b border-[#E5E7EB] bg-[#F7F8FA]">
                <CardTitle className="text-base font-semibold text-[#1A1A1A]">
                  All Users
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {users.length === 0 ? (
                  <p className="text-[#6B7280] text-center py-8">No users found</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F7F8FA] border-b border-[#E5E7EB]">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">User</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Role</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Status</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Last Login</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Created</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E7EB]">
                        {users.map((user) => (
                          <tr key={user.id} data-testid={`user-row-${user.id}`} className="hover:bg-[#F7F8FA] transition-colors">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#7B6FE8] rounded-full flex items-center justify-center text-white font-semibold">
                                  {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                  <p className="font-medium text-[#1A1A1A]">
                                    {user.name}
                                    {user.id === currentUser?.id && (
                                      <span className="ml-2 text-xs text-[#6B7280]">(You)</span>
                                    )}
                                  </p>
                                  <p className="text-sm text-[#6B7280]">{user.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <Badge className={`text-xs px-2 py-1 rounded-full border ${
                                user.role === 'super_admin' 
                                  ? 'bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]'
                                  : user.role === 'viewer'
                                  ? 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]'
                                  : 'bg-[#EEF0FF] text-[#6B5CE7] border-[#C4BFFF]'
                              }`}>
                                {ROLE_OPTIONS.find(r => r.value === user.role)?.label || user.role}
                              </Badge>
                            </td>
                            <td className="px-4 py-4">
                              <Badge className={`text-xs px-2 py-1 rounded-full border ${
                                user.active !== false
                                  ? 'bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]'
                                  : 'bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]'
                              }`}>
                                {user.active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-2 text-sm text-[#6B7280]">
                                <Clock className="h-4 w-4" />
                                {formatDate(user.last_login)}
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className="text-sm text-[#6B7280]">{formatDate(user.created_at)}</span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  data-testid={`view-activity-${user.id}-btn`}
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#6B7280] hover:text-[#7B6FE8] hover:bg-[#EEF0FF]"
                                  onClick={() => handleViewActivity(user)}
                                >
                                  <Activity className="h-4 w-4" />
                                </Button>
                                <Button
                                  data-testid={`edit-user-${user.id}-btn`}
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6]"
                                  onClick={() => handleEdit(user)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {user.id !== currentUser?.id && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        data-testid={`delete-user-${user.id}-btn`}
                                        variant="ghost"
                                        size="sm"
                                        className="text-[#6B7280] hover:text-red-600 hover:bg-red-50"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete User</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to delete <strong>{user.name}</strong>? This action cannot be undone.
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => handleDelete(user.id)} className="bg-red-600 hover:bg-red-700">
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invites Tab */}
          <TabsContent value="invites">
            <Card className="bg-white border border-[#E5E7EB] shadow-none">
              <CardHeader className="border-b border-[#E5E7EB] bg-[#F7F8FA]">
                <CardTitle className="text-base font-semibold text-[#1A1A1A]">
                  All Invitations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {invites.length === 0 ? (
                  <div className="text-center py-12">
                    <Send className="h-12 w-12 text-[#D1D5DB] mx-auto mb-3" />
                    <p className="text-[#6B7280]">No invitations sent yet</p>
                    <Button
                      onClick={() => setInviteDialogOpen(true)}
                      className="mt-4 bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Send First Invite
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-[#F7F8FA] border-b border-[#E5E7EB]">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Invitee</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Role</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Status</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Invited By</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Expires</th>
                          <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#E5E7EB]">
                        {invites.map((invite) => (
                          <tr key={invite.id} data-testid={`invite-row-${invite.id}`} className="hover:bg-[#F7F8FA] transition-colors">
                            <td className="px-4 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-[#6B7280] rounded-full flex items-center justify-center text-white font-semibold">
                                  {invite.name?.charAt(0)?.toUpperCase() || 'U'}
                                </div>
                                <div>
                                  <p className="font-medium text-[#1A1A1A]">{invite.name}</p>
                                  <p className="text-sm text-[#6B7280]">{invite.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <Badge className={`text-xs px-2 py-1 rounded-full border ${
                                invite.role === 'super_admin' 
                                  ? 'bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]'
                                  : invite.role === 'viewer'
                                  ? 'bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]'
                                  : 'bg-[#EEF0FF] text-[#6B5CE7] border-[#C4BFFF]'
                              }`}>
                                {ROLE_OPTIONS.find(r => r.value === invite.role)?.label || invite.role}
                              </Badge>
                            </td>
                            <td className="px-4 py-4">
                              {getInviteStatusBadge(invite)}
                            </td>
                            <td className="px-4 py-4">
                              <span className="text-sm text-[#6B7280]">{invite.invited_by_email}</span>
                            </td>
                            <td className="px-4 py-4">
                              <span className="text-sm text-[#6B7280]">{formatDate(invite.expires_at)}</span>
                            </td>
                            <td className="px-4 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                {invite.status === 'pending' && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-[#6B7280] hover:text-[#7B6FE8] hover:bg-[#EEF0FF]"
                                    onClick={() => handleResendInvite(invite.id)}
                                  >
                                    <RefreshCw className="h-4 w-4" />
                                  </Button>
                                )}
                                {invite.status !== 'used' && (
                                  <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                      <Button variant="ghost" size="sm" className="text-[#6B7280] hover:text-red-600 hover:bg-red-50">
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                      <AlertDialogHeader>
                                        <AlertDialogTitle>Delete Invitation</AlertDialogTitle>
                                        <AlertDialogDescription>
                                          Are you sure you want to delete the invitation for <strong>{invite.email}</strong>?
                                        </AlertDialogDescription>
                                      </AlertDialogHeader>
                                      <AlertDialogFooter>
                                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => handleDeleteInvite(invite.id)} className="bg-red-600 hover:bg-red-700">
                                          Delete
                                        </AlertDialogAction>
                                      </AlertDialogFooter>
                                    </AlertDialogContent>
                                  </AlertDialog>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Create/Edit User Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-[#1A1A1A]">
                {editingUser ? 'Edit User' : 'Add New User'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-[#1A1A1A] font-medium">Full Name *</Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="mt-1.5 border-[#E5E7EB]"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <Label htmlFor="email" className="text-[#1A1A1A] font-medium">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="user-email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="mt-1.5 border-[#E5E7EB]"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <Label htmlFor="password" className="text-[#1A1A1A] font-medium">
                  Password {editingUser ? '(leave blank to keep current)' : '*'}
                </Label>
                <Input
                  id="password"
                  type="password"
                  data-testid="user-password-input"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editingUser}
                  className="mt-1.5 border-[#E5E7EB]"
                  placeholder="••••••••"
                />
              </div>
              
              <div>
                <Label className="text-[#1A1A1A] font-medium">Role *</Label>
                <div className="mt-2 space-y-2">
                  {ROLE_OPTIONS.map((role) => (
                    <div
                      key={role.value}
                      data-testid={`role-option-${role.value}`}
                      onClick={() => setFormData({...formData, role: role.value})}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${
                        formData.role === role.value
                          ? 'border-[#7B6FE8] bg-[#EEF0FF]'
                          : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${formData.role === role.value ? 'text-[#6B5CE7]' : 'text-[#1A1A1A]'}`}>
                            {role.label}
                          </p>
                          <p className="text-sm text-[#6B7280]">{role.description}</p>
                        </div>
                        {formData.role === role.value && (
                          <div className="w-5 h-5 bg-[#7B6FE8] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {editingUser && (
                <div className="flex items-center justify-between p-3 bg-[#F7F8FA] rounded-lg">
                  <div>
                    <p className="font-medium text-[#1A1A1A]">Account Status</p>
                    <p className="text-sm text-[#6B7280]">{formData.active ? 'User can log in' : 'User cannot log in'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, active: !formData.active})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.active ? 'bg-[#7B6FE8]' : 'bg-[#D1D5DB]'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.active ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              )}

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E5E7EB]">
                  Cancel
                </Button>
                <Button type="submit" data-testid="save-user-btn" className="bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white">
                  {editingUser ? 'Save Changes' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Invite User Dialog */}
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
                <Send className="h-5 w-5 text-[#7B6FE8]" />
                Invite New User
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleInviteSubmit} className="space-y-4">
              <p className="text-sm text-[#6B7280]">
                Send an email invitation to a new admin user. They will receive a secure link to create their account.
              </p>
              
              <div>
                <Label htmlFor="invite-name" className="text-[#1A1A1A] font-medium">Full Name *</Label>
                <Input
                  id="invite-name"
                  data-testid="invite-name-input"
                  value={inviteFormData.name}
                  onChange={(e) => setInviteFormData({...inviteFormData, name: e.target.value})}
                  required
                  className="mt-1.5 border-[#E5E7EB]"
                  placeholder="John Doe"
                />
              </div>
              
              <div>
                <Label htmlFor="invite-email" className="text-[#1A1A1A] font-medium">Email Address *</Label>
                <Input
                  id="invite-email"
                  type="email"
                  data-testid="invite-email-input"
                  value={inviteFormData.email}
                  onChange={(e) => setInviteFormData({...inviteFormData, email: e.target.value})}
                  required
                  className="mt-1.5 border-[#E5E7EB]"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <Label className="text-[#1A1A1A] font-medium">Role *</Label>
                <div className="mt-2 space-y-2">
                  {ROLE_OPTIONS.map((role) => (
                    <div
                      key={role.value}
                      onClick={() => setInviteFormData({...inviteFormData, role: role.value})}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${
                        inviteFormData.role === role.value
                          ? 'border-[#7B6FE8] bg-[#EEF0FF]'
                          : 'border-[#E5E7EB] hover:border-[#D1D5DB]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`font-medium ${inviteFormData.role === role.value ? 'text-[#6B5CE7]' : 'text-[#1A1A1A]'}`}>
                            {role.label}
                          </p>
                          <p className="text-sm text-[#6B7280]">{role.description}</p>
                        </div>
                        {inviteFormData.role === role.value && (
                          <div className="w-5 h-5 bg-[#7B6FE8] rounded-full flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-lg p-3">
                <p className="text-sm text-[#92400E]">
                  <strong>Note:</strong> The invitation link will expire in 7 days. You can resend the invite if needed.
                </p>
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setInviteDialogOpen(false)} className="border-[#E5E7EB]">
                  Cancel
                </Button>
                <Button type="submit" data-testid="send-invite-btn" className="bg-[#7B6FE8] hover:bg-[#6B5CE7] text-white">
                  <Send className="h-4 w-4 mr-2" />
                  Send Invitation
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Activity Log Dialog */}
        <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-[#1A1A1A] flex items-center gap-2">
                <Activity className="h-5 w-5 text-[#7B6FE8]" />
                Activity Log - {selectedUserActivity?.name}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-y-auto">
              {loadingActivity ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#7B6FE8]"></div>
                </div>
              ) : activityLogs.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 text-[#D1D5DB] mx-auto mb-3" />
                  <p className="text-[#6B7280]">No activity recorded for this user</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {activityLogs.map((log, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-[#F7F8FA] rounded-lg border border-[#E5E7EB]">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className={`w-2 h-2 rounded-full ${
                          log.action.includes('login') ? 'bg-[#7B6FE8]' :
                          log.action.includes('created') ? 'bg-[#10B981]' :
                          log.action.includes('updated') ? 'bg-[#F59E0B]' :
                          log.action.includes('deleted') ? 'bg-[#EF4444]' :
                          log.action.includes('sent') || log.action.includes('invite') ? 'bg-[#0EA5E9]' :
                          'bg-[#6B7280]'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`text-xs px-2 py-0.5 rounded border ${getActionBadgeColor(log.action)}`}>
                            {log.action.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-[#6B7280]">on {log.entity_type}</span>
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <p className="text-sm text-[#6B7280] mt-1 truncate">
                            {JSON.stringify(log.details).slice(0, 100)}
                            {JSON.stringify(log.details).length > 100 && '...'}
                          </p>
                        )}
                        <p className="text-xs text-[#9CA3AF] mt-1">{formatDate(log.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter className="pt-4 border-t border-[#E5E7EB]">
              <Button variant="outline" onClick={() => setActivityDialogOpen(false)} className="border-[#E5E7EB]">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminUsers;
